/**
 * hl-cart-bridge.js
 *
 * Conecta los formularios propios de las secciones HL (PDP hero, sticky ATC,
 * order bumps y upsells del drawer) con el carrito NATIVO del theme.
 *
 * No dibuja ningún panel: hace el add por AJAX y dispara el mismo evento
 * estándar que usa Horizon (`CartLinesUpdateEvent`). A partir de ahí el theme
 * se encarga solo de re-renderizar el drawer, actualizar el contador del
 * header y abrir el panel.
 *
 * Los formularios nativos (los que viven dentro de <product-form-component>)
 * se ignoran: ya los maneja el theme.
 */

import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const ADD_URL = window.Theme?.routes?.cart_add_url || '/cart/add.js';
const CART_URL = window.Theme?.routes?.cart_url || '/cart';

/** IDs de las secciones de carrito presentes en la página (drawer y/o página /cart). */
function cartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    if (el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return [...ids];
}

/**
 * Apps de bundles (Kaching y similares) inyectan sus propios inputs ocultos
 * al final del form en vez de reemplazar los existentes. Como FormData.get()
 * devuelve el PRIMERO, hay que quedarse con el último para respetar la oferta
 * elegida; si no, siempre se agrega 1 unidad de la variante base.
 */
function lastValue(formData, key) {
  const all = formData.getAll(key);
  return all.length ? all[all.length - 1] : null;
}

/** Convierte un <form> de producto en items para /cart/add.js */
function itemsFromForm(form) {
  const fd = new FormData(form);

  // Formato multilínea: items[0][id], items[0][quantity]…
  const grouped = {};
  for (const [key, value] of fd.entries()) {
    const match = key.match(/^items\[(\d+)\]\[(\w+)\]$/);
    if (!match) continue;
    const [, index, field] = match;
    grouped[index] = grouped[index] || {};
    grouped[index][field] = value;
  }

  const grouped_items = Object.values(grouped)
    .map((raw) => ({ id: Number(raw.id), quantity: Number(raw.quantity) || 1 }))
    .filter((item) => item.id);

  if (grouped_items.length) return grouped_items;

  const id = Number(lastValue(fd, 'id'));
  if (!id) return [];

  const item = { id, quantity: Number(lastValue(fd, 'quantity')) || 1 };

  const sellingPlan = lastValue(fd, 'selling_plan');
  if (sellingPlan) item.selling_plan = Number(sellingPlan);

  const properties = {};
  for (const [key, value] of fd.entries()) {
    const match = key.match(/^properties\[(.+)\]$/);
    if (match && String(value).trim() !== '') properties[match[1]] = value;
  }
  if (Object.keys(properties).length) item.properties = properties;

  return [item];
}

/** Order bumps marcados en la página (sección HL · Order bumps). */
function bumpItems() {
  return [...document.querySelectorAll('[data-hl-bump][data-on="true"]')]
    .map((el) => ({ id: Number(el.getAttribute('data-hl-bump')), quantity: 1 }))
    .filter((item) => item.id);
}

/** Suma cantidades de variantes repetidas para no mandar líneas duplicadas. */
function mergeItems(items) {
  const merged = [];
  for (const item of items) {
    const twin = merged.find(
      (candidate) =>
        candidate.id === item.id &&
        candidate.selling_plan === item.selling_plan &&
        JSON.stringify(candidate.properties) === JSON.stringify(item.properties)
    );
    if (twin) twin.quantity += item.quantity;
    else merged.push({ ...item });
  }
  return merged;
}

/**
 * Agrega al carro y deja que el theme abra y refresque su propio drawer.
 * @param {Array<object>} rawItems
 * @param {Element} [source] elemento desde el que se dispara el evento
 */
async function addToCart(rawItems, source, options = {}) {
  const items = mergeItems(rawItems);
  if (!items.length) return;

  const target = source instanceof Element ? source : document;
  const itemCount = items.reduce((total, item) => total + item.quantity, 0);
  const { beforeOpen, waitForExternal } = options;

  try {
    const response = await fetch(ADD_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: cartSectionIds().join(',') }),
    }).then((res) => res.json());

    // Falló: avisamos y no abrimos nada.
    if (response.status) {
      document.dispatchEvent(
        new CartErrorEvent({
          error: response.message || 'Add to cart failed',
          code: 'INVALID',
          detail: { description: response.description, errors: response.errors },
        })
      );
      throw new AddToCartRejection(response.message);
    }

    const cart = waitForExternal ? await settleCart() : await readCart();

    // ── Acá corre la animación del botón. Recién cuando termina
    //    disparamos el evento, que es lo que abre el carrito. ──
    if (typeof beforeOpen === 'function') await beforeOpen();

    const deferred = CartLinesUpdateEvent.createPromise();

    target.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        didError: false,
        items: cart.items,
        source: 'hl-cart-bridge',
        itemCount,
        sections: response.sections,
      },
    });

    if (cart.item_count === 0) writeUntil(0);
    else resetTimer();

    requestAnimationFrame(() => requestAnimationFrame(popCartCount));
  } catch (error) {
    if (error instanceof AddToCartRejection) throw error;

    document.dispatchEvent(
      new CartErrorEvent({
        error: error?.message || 'Network error during add to cart',
        code: 'SERVICE_UNAVAILABLE',
      })
    );
    throw error;
  }
}

/* ---------- Submit de los formularios propios ---------- */

/**
 * ¿Hay una app de bundles en esta página? (Kaching y similares)
 *
 * Estas apps inyectan inputs ocultos en el form y agregan sus unidades de
 * regalo en una llamada aparte, después de la nuestra. Cuando detectamos una,
 * esperamos a que el carrito se estabilice antes de abrirlo.
 *
 * Forzar: window.HL_EXTERNAL_CART = true / false
 */
function hasExternalCartApp(form) {
  if (window.HL_EXTERNAL_CART === false) return false;
  if (window.HL_EXTERNAL_CART === true) return true;

  const scope = form?.closest('.hl-buy') || document;
  const appBlocks = scope.querySelector('.hl-appblocks');

  if (appBlocks && appBlocks.children.length > 0) return true;
  return Boolean(document.querySelector('[class*="kaching" i], [id*="kaching" i]'));
}

/** Lee el carrito actual. */
async function readCart() {
  return fetch(`${CART_URL}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  }).then((res) => res.json());
}

/**
 * Las apps de bundles agregan sus unidades de regalo en una llamada aparte,
 * después de la nuestra. Esperamos a que el conteo se repita dos lecturas
 * seguidas para no abrir el carrito a medio armar.
 */
async function settleCart() {
  let cart = await readCart();

  for (let i = 0; i < 6; i++) {
    await wait(300);
    const next = await readCart();
    if (next.item_count === cart.item_count) return next;
    cart = next;
  }

  return cart;
}

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.action.includes('/cart/add')) return;
    // Los forms del theme ya se manejan solos.
    if (form.closest('product-form-component')) return;

    /* Siempre. Kaching engancha el click del botón, no el submit: si no
       cortamos acá, el navegador hace el POST nativo y termina en /cart. */
    event.preventDefault();

    const button = form.querySelector('[type="submit"]');
    setLoading(button);

    /* La barra sticky lee del form principal: las apps de bundles solo
       enganchan el primer form de la página, así que el sticky por su cuenta
       agregaría la variante base ignorando la oferta seleccionada. */
    const mainForm = document.getElementById('hl-form');
    const sourceForm = form.id === 'hl-sticky-form' && mainForm ? mainForm : form;
    const isMainForm = sourceForm === mainForm;

    // El botón principal del PDP arrastra los order bumps marcados.
    const items = [...itemsFromForm(sourceForm), ...(isMainForm ? bumpItems() : [])];

    addToCart(items, form, {
      beforeOpen: () => playSuccess(button),
      waitForExternal: hasExternalCartApp(sourceForm),
    })
      .catch(() => resetButton(button))
      .finally(() => releaseButton(button));
  },
  true
);

/* ---------- Botones "Agregar" de los upsells dentro del drawer ---------- */

document.addEventListener('click', (event) => {
  const button = event.target.closest?.('[data-hl-cart-add]');
  if (!button) return;

  event.preventDefault();
  const id = Number(button.getAttribute('data-hl-cart-add'));
  if (!id) return;

  setLoading(button);
  addToCart([{ id, quantity: 1 }], button, { beforeOpen: () => playSuccess(button) })
    .catch(() => resetButton(button))
    .finally(() => releaseButton(button));
});

/* ---------- Links al carrito abren el drawer nativo ---------- */

document.addEventListener('click', (event) => {
  const link = event.target.closest?.('a[href$="/cart"], a[href*="/cart?"]');
  if (!link || link.hasAttribute('data-hl-cart-ignore')) return;

  const drawer = document.querySelector('theme-drawer#cart-drawer');
  if (!drawer?.open) return;

  event.preventDefault();
  drawer.open();
});

/* ============================================================
   UX: velo, animaciones y contador de reserva
   ============================================================ */

const TIMER_KEY = 'hl-cart-reserve-until';

function drawer() {
  return document.querySelector('theme-drawer#cart-drawer');
}

/** Duración de la animación de éxito antes de abrir el carrito (ms). */
const SUCCESS_MS = 700;

/** Error propio: ya se avisó al usuario, no hace falta volver a reportarlo. */
class AddToCartRejection extends Error {}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** Estado "procesando": spinner sin mover el ancho del botón. */
function setLoading(button) {
  if (!button) return;
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  button.classList.add('is-loading');
}

/**
 * Estado "listo": el botón se pone verde con un tilde.
 * Devuelve una promesa que recién resuelve cuando termina la animación,
 * así el carrito se abre después y no encima.
 */
function playSuccess(button) {
  if (!button) return Promise.resolve();
  button.classList.remove('is-loading');
  button.classList.add('is-added');
  return wait(SUCCESS_MS);
}

/** Vuelve al estado normal después de un fallo. */
function resetButton(button) {
  button?.classList.remove('is-loading', 'is-added');
}

/** Libera el botón; el verde se queda un momento más y luego se va. */
function releaseButton(button) {
  if (!button) return;
  button.disabled = false;
  button.removeAttribute('aria-busy');
  button.classList.remove('is-loading');
  setTimeout(() => button.classList.remove('is-added'), 600);
}

/** Rebote del contador del header y del badge del drawer. */
function popCartCount() {
  const targets = [document.querySelector('cart-icon'), ...document.querySelectorAll('.theme-drawer__badge')];
  for (const el of targets) {
    if (!el) continue;
    el.classList.remove('hl-pop');
    void el.offsetWidth; // reinicia la animación
    el.classList.add('hl-pop');
    setTimeout(() => el.classList.remove('hl-pop'), 600);
  }
}

/* Click en el velo cierra el carrito. En escritorio el diálogo no es modal,
   así que el theme no escucha clicks afuera. */
document.addEventListener('click', (event) => {
  if (!event.target.closest?.('[data-hl-cart-scrim]')) return;
  drawer()?.close?.();
});

/* ---------- Contador de reserva ---------- */

function readUntil() {
  try {
    return Number(localStorage.getItem(TIMER_KEY)) || 0;
  } catch {
    return 0;
  }
}

function writeUntil(value) {
  try {
    if (value) localStorage.setItem(TIMER_KEY, String(value));
    else localStorage.removeItem(TIMER_KEY);
  } catch {
    /* modo privado: el contador arranca de cero en cada visita */
  }
}

function tickTimer() {
  const el = document.querySelector('[data-hl-timer]');
  if (!el) return;

  const minutes = Number(el.dataset.minutes) || 10;
  const window_ms = minutes * 60000;
  const now = Date.now();
  let until = readUntil();

  // Sin reserva vigente, o una guardada de otra sesión: arranca de nuevo.
  if (!until || until > now + window_ms) {
    until = now + window_ms;
    writeUntil(until);
  }

  const left = Math.max(0, until - now);
  const count = el.querySelector('[data-hl-timer-count]');
  const labelEl = el.querySelector('[data-hl-timer-label]');

  if (left === 0) {
    el.classList.add('is-expired');
    el.classList.remove('is-urgent');
    if (labelEl && el.dataset.expired) labelEl.textContent = el.dataset.expired;
    return;
  }

  el.classList.remove('is-expired');
  el.classList.toggle('is-urgent', left <= 60000);
  if (labelEl && el.dataset.label) labelEl.textContent = el.dataset.label;

  const minutesLeft = Math.floor(left / 60000);
  const secondsLeft = Math.floor((left % 60000) / 1000);
  if (count) count.textContent = `${minutesLeft}:${String(secondsLeft).padStart(2, '0')}`;
}

setInterval(tickTimer, 1000);
document.addEventListener('theme-drawer:open', tickTimer);
tickTimer();

/** Reinicia la reserva: se llama en cada alta al carro. */
function resetTimer() {
  writeUntil(0);
  tickTimer();
}

window.HLCart = { add: addToCart };
